import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/utils/api";
import { storeTokenPair } from "@/utils/tokenRefresh";
import { API_LEGACY } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/shared";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { AppleSignInButton } from "@/components/auth/AppleSignInButton";

const LoginPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const processSessionId = useCallback(async (hash) => {
    setIsProcessing(true);
    
    try {
      // Extract session_id from URL fragment
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");
      
      if (!sessionId) {
        setIsProcessing(false);
        return;
      }

      // Exchange session_id for user data
      const response = await api.post(`/auth/session`, {
        session_id: sessionId
      });

      const { user, session_token, access_token, refresh_token } = response.data;

      // Persist full Phase-2 token pair + legacy session_token for compat.
      storeTokenPair({ access_token, refresh_token, session_token });
      localStorage.setItem("user", JSON.stringify(user));

      // Clean URL fragment
      window.history.replaceState(null, "", window.location.pathname);

      toast.success(`Welcome back, ${user.name}!`);
      
      // Redirect to dashboard with full page reload
      window.location.href = '/';
    } catch (error) {
      toast.error("Failed to authenticate. Please try again.");
      setIsProcessing(false);
    }
  }, []);

  const checkExistingSession = useCallback(async () => {
    const sessionToken = localStorage.getItem("session_token");
    const userStr = localStorage.getItem("user");
    
    if (!sessionToken || !userStr) {
      return; // No session, show login page
    }

    // If we have both session and user, redirect to dashboard
    // Don't validate via API on login page - let dashboard handle that
    window.location.href = '/';
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setIsLoggingIn(true);
    
    try {
      const response = await api.post(`/auth/login`, null, {
        params: { email, password }
      });
      
      // Persist full Phase-2 token pair + legacy session_token for compat.
      storeTokenPair({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        session_token: response.data.session_token,
      });
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(`Welcome back, ${response.data.user.name}!`);
      
      // Use window.location to force a full page reload and redirect
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
      setIsLoggingIn(false);
    }
  };

  // Direct Google Sign-In uses REDIRECT mode (`ux_mode="redirect"`).
  // The button posts directly to the backend `/api/auth/google/web/callback`
  // endpoint which returns an inline HTML handoff that writes the JWT to
  // localStorage and replace-navigates into the app.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

  useEffect(() => {
    // Check if we have a session_id in the URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      processSessionId(hash);
      return;
    }
    // Surface a Google redirect-mode error from the backend callback
    // (e.g. /login?google_error=csrf|invalid_token|forbidden|server).
    const search = new URLSearchParams(window.location.search);
    const googleError = search.get("google_error");
    if (googleError) {
      const messages = {
        csrf: "Sign-in was rejected (CSRF check failed). Please try again.",
        invalid_token: "Google rejected the sign-in. Please try again.",
        forbidden: "This Google account isn't authorized to access Travel Gadjet yet.",
        server: "Something went wrong on our side. Please try again in a moment.",
      };
      toast.error(messages[googleError] || "Sign-in failed. Please try again.");
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }
    // Check if user already has a valid session
    checkExistingSession();
  }, [processSessionId, checkExistingSession]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-sky-600 mb-4" />
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4 pb-8">
          {/* Full brand lockup — notebook glyph + wordmark + tagline. No
              background panel, inherits the card surface for a clean look
              in both light and dark modes. */}
          <BrandLogo variant="lockup" />
        </CardHeader>
        <CardContent className="space-y-6">
          {!showEmailLogin ? (
            <div className="space-y-4">
              {/* Primary — direct Google Sign-In via Google Identity
                  Services REDIRECT mode. Avoids the Safari/COOP popup
                  issues that intermittently break popup-based GIS, and
                  works reliably across every browser including
                  Safari + Private Relay.
                  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

                  We wrap GoogleLogin in our own GoogleSignInButton to
                  give it identical visual treatment to the Apple button
                  (Google's own branded button has a hard-coded white
                  G-icon container in its dark theme — the wrapper makes
                  both buttons look pure-black in dark mode and
                  pure-white in light mode while still using GIS for
                  the actual redirect handling).

                  Note: the wrapper component uses API_LEGACY for the
                  OAuth redirect URI — OAuth redirect URIs are a stable
                  contract registered in Google Cloud Console; coupling
                  them to our API version (`/api/v1/...`) would force a
                  Console change every time we cut a new major API
                  version. The backend serves the same handler on both
                  paths. */}
              <GoogleSignInButton />

              {/* Sign in with Apple — popup-mode flow that posts id_token
                  to /api/auth/apple/web. Renders only when the Apple env
                  vars are configured; mirrors the Google width for visual
                  alignment. */}
              <AppleSignInButton />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={() => setShowEmailLogin(true)}
                  variant="outline"
                  className="w-[320px] h-[44px]"
                  data-testid="email-login-toggle-btn"
                >
                  Sign in with Email
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoggingIn}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoggingIn}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoggingIn}
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-sky-600 hover:text-sky-700 underline-offset-4 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Button
                type="button"
                onClick={() => setShowEmailLogin(false)}
                variant="ghost"
                className="w-full"
              >
                Back to Google Sign In
              </Button>
            </form>
          )}
          
          <div className="text-center text-sm text-gray-500">
            <p>Secure authentication</p>
            <p className="mt-2 text-xs">Email login available for shared trip users</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
